import { compareDecimal, isNonZero, multiplyDecimals } from '../../utils/decimal';
import type { ShipmentLeg, ShippingAgent } from '../../types/Procurement';

/**
 * What to fill in on a shipment when its agent or leg changes. The agent's China
 * warehouse is where the seller delivers, so it only applies to the China-side
 * leg, and anything already typed is left alone.
 */
export function deliveryPrefill(
  agent: ShippingAgent | undefined,
  leg: ShipmentLeg | '' | null | undefined,
  currentAddress: string,
): { delivery_address?: string } {
  const address = agent?.warehouse_china?.address?.trim();
  if (leg !== 'domestic-origin' || !address || currentAddress.trim()) return {};
  return { delivery_address: address };
}

export interface AgentQuote {
  agent: ShippingAgent;
  /** The agent's rate for the unit it charges by, or null when none is set. */
  rate: string | null;
  unit: 'CBM' | 'kg';
  currency: string | null;
  /** Rate times the shipment's size, or null when there is no rate or no size yet. */
  amount: string | null;
}

/** One quote per active agent: air is charged per kg, everything else per CBM. Priced in the agent's own currency, never converted. */
export function agentQuotes(agents: ShippingAgent[], size: { cbm: string | null; kg: string | null }): AgentQuote[] {
  const quotes = agents.filter(a => a.active).map((agent): AgentQuote => {
    const perKg = agent.shipping_mode === 'air';
    const rawRate = perKg ? agent.rate_per_kg : agent.rate_per_cbm;
    const rate = isNonZero(rawRate) ? rawRate : null;
    const amount = rate ? multiplyDecimals(rate, perKg ? size.kg : size.cbm) : null;
    return { agent, rate, unit: perKg ? 'kg' : 'CBM', currency: agent.currency, amount };
  });
  const pricedFirst = (a: AgentQuote, b: AgentQuote) => (a.amount === null ? 1 : 0) - (b.amount === null ? 1 : 0);
  return quotes.sort((a, b) =>
    pricedFirst(a, b)
    || (a.currency ?? '').localeCompare(b.currency ?? '')
    || (compareDecimal(a.amount, b.amount) ?? 0)
    || a.agent.name.localeCompare(b.agent.name));
}
